import { Box, MenuItem, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLiveGraph, useLiveStreamValue } from '../LiveGraphStore';
import DUOrientationSVG from './DUOrientationSVG';
import { useWidgetHeader } from './WidgetHeaderContext';
import { usePersistedWidgetStyle } from './usePersistedWidgetStyle';
import RawDataWidget from './RawDataWidget';
import LiveNumberWidget from './LiveNumberWidget';

interface OrientationWidgetProps {
  id: string;
  style: string;
}

const OrientationWidget = ({ id, style }: OrientationWidgetProps) => {
  const { t } = useTranslation();
  const angle = useLiveStreamValue('Locator Yaw', 0);
  const orientationGraph = useLiveGraph('locator');

  const { widgetStyle, handleSetStyle, styleMenuItems } = usePersistedWidgetStyle(id, style);

  const title = useMemo(() => t('widgetBase.drive-unit-orientation', 'Drive Unit Orientation'), [t]);

  const menuItems = useMemo(
    () => (
      <>
        <MenuItem onClick={() => handleSetStyle('default')}>
          <Trans i18nKey="orientationWidget.menuItem.default">Orientation View</Trans>
        </MenuItem>
        {styleMenuItems}
      </>
    ),
    [handleSetStyle, styleMenuItems]
  );

  useWidgetHeader({ title, menuItems }, [title, menuItems]);

  if (widgetStyle === 'raw') {
    return <RawDataWidget graph={orientationGraph} />;
  }

  if (widgetStyle === 'number') {
    return <LiveNumberWidget graph={orientationGraph} />;
  }

  return (
    <Box
      flex="1"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      justifyContent="center"
      alignItems="center"
      gap={2}
      pb={1}
      width="100%"
      height="100%"
    >
      <DUOrientationSVG angle={angle} />
      <Box
        sx={{
          px: 3,
          py: 1,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          minWidth: 96,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" component="div">
          {Math.round(angle)}°
        </Typography>
      </Box>
    </Box>
  );
};

export default OrientationWidget;
